import React, { FC, FormEvent, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { fromCentsWithLocale } from 'utils/Currency';
import { useCheckoutCurrency, useCheckoutSessionContext } from 'hooks/context/checkoutSession';
import { useModals } from 'hooks/context/modals';
import { useGiftCards } from 'hooks/context/giftcards';
import { useCalcContext } from 'hooks/context/calc';
import { getGiftCardDeductions } from 'utils/GiftCards';
import { CheckoutType } from 'services/checkout-service/models';

import { MODAL_TYPES } from 'components/PageLayout/Modals/types';

import { GiftCardPaymentInfo } from '../GiftCardPaymentInfo';
import { useCloverPay } from '../CloverPaymentSection/useCloverPay';
import { useGiftCardPay } from '../CloverPaymentSection/useGiftCardPay';

import styles from './styles.module.scss';

type GiftCardSectionProps = {
  cloverElementsInstance: Clover.ElementRoot;
  cloverInstance: Clover;
};

export const GiftCardSection: FC<GiftCardSectionProps> = ({cloverElementsInstance, cloverInstance}) => {
  const GIFT_CARD_LIMIT = 5;
  const [t] = useTranslation();
  const { tokenize } = useCloverPay();
  const { balanceInquiry } = useGiftCardPay();
  const { checkoutSession } = useCheckoutSessionContext();
  const { setModalType } = useModals();
  const { payableTotalAmount, convenienceFee = 0, tipAmount = 0, pendingAmount } = useCalcContext();
  const currency = useCheckoutCurrency();
  const { giftCards, setGiftCards } = useGiftCards();
  const [isApplyEnabled, setIsApplyEnabled] = useState<boolean>(false);
  const [showInputs, setShowInputs] = useState(false);
  const { type } = checkoutSession || {};
  const isPayWidget = useMemo(() => type === CheckoutType.PAY_WIDGETS, [type]);

  const removeCardEntry = async (e: FormEvent, gcToken?: string) => {
    e.preventDefault();
    if (gcToken) {
      setGiftCards(giftCards.filter((gc) => gc.token !== gcToken));
    } else {
      setShowInputs(false);
    }
  }

  const addGiftCard = async ( e: FormEvent) => {
    e.preventDefault();
    if (!checkoutSession?.id) {
      console.error("Cannot do balance inquiry without checkout session id.");
      return;
    }

    try {
      const giftCardTokenResponse = await tokenize(cloverInstance, false, true);
      const {
        giftcard,
        token
      } = giftCardTokenResponse;

      const balanceInquiryResponse = await balanceInquiry(checkoutSession?.id, token, isPayWidget);

      if (balanceInquiryResponse.giftCard.balance <= 0) {
        setModalType(MODAL_TYPES.GIFT_CARD_ZERO_BALANCE);
        return;
      }

      giftCards.push({
        ...balanceInquiryResponse.giftCard,
        last4: giftcard?.last4?.toString(),
        token,
      });

      setGiftCards([...giftCards]);
      setShowInputs(false);
    } catch (e) {
      setModalType(MODAL_TYPES.GIFT_CARD_GENERIC_ERROR);
    }
  }

  const toggleShowInputs = () => {
    setShowInputs(!showInputs);
  }

  const giftCardDeductions = getGiftCardDeductions(payableTotalAmount + tipAmount + convenienceFee, giftCards);
  const giftCardCount = (giftCardDeductions && giftCardDeductions?.length) || 0;

  return (
    <section className={styles.giftCardSection}>
      <header>
        <h3 className={styles.sectionInfo}>{t('giftCard.title')} {giftCardCount >= 1 ? `(${giftCardCount}/${GIFT_CARD_LIMIT})` : ''}</h3>
        {!showInputs &&
          <button
            className={styles.add}
            onClick={toggleShowInputs}
            disabled={giftCardCount >= GIFT_CARD_LIMIT || pendingAmount === 0}
          >
            {t('giftCard.add')}
          </button>
        }
      </header>

      {showInputs && cloverElementsInstance && (
        <div className={styles.giftCardForm}>
          <GiftCardPaymentInfo
            cloverElementsInstance={cloverElementsInstance}
            toggleButtonDisableState={setIsApplyEnabled}
            isButtonDisabled={!isApplyEnabled}
          />
          <div className={styles.giftCardFormButtons}>
            <button className={styles.remove} onClick={removeCardEntry}>
              {t('giftCard.remove')}
            </button>
            <button disabled={!isApplyEnabled} className={styles.apply} onClick={addGiftCard}>
              {t('giftCard.apply')}
            </button>
          </div>
        </div>
      )}

      {giftCardDeductions && giftCardDeductions.length > 0 && (
        <ul className={styles.cardList}>
          {giftCardDeductions
            .map(({ token, last4, amountUsedFromGiftCard }) => (
              <li key={token}>
                <div className={styles.giftCardBlurb}>
                  {t('giftCard.cardBlurb', {
                    last4: last4,
                    amount: fromCentsWithLocale(amountUsedFromGiftCard, currency)
                  })}
                </div>
                <button className={styles.remove} onClick={(e: FormEvent) => removeCardEntry(e, token)}>
                  {t('giftCard.remove')}
                </button>
              </li>
            ))
          }
        </ul>
      )}
    </section>
  );
};
